import EditIcon from '@mui/icons-material/Edit'
import { TableCell, Link, Box, Chip, Checkbox, TableRow, styled } from '@mui/material'
import OverflowText from 'components/ui/OverflowText'
import StatusBadge from 'components/ui/StatusBadge'
import { DocListItem } from 'entities/Documents'
import PropTypes from 'prop-types'
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom'
import { timeWithoutYear } from 'utils/format'

const NameSpan = styled('span')(({ theme }) => ({
  display: 'flex',
  flex: 1,
  width: 1,
  alignItems: 'center',
  color: theme.palette.common.black,
  overflow: 'hidden',
  paddingRight: 14 + theme.spacing(1),
}))

const EditButton = styled(EditIcon)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  fontSize: '1em',
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.primary.main,
  },
}))

const MyChip = ({ userName }) =>
  userName ? <Chip size="small" label={userName.charAt(0)} color="primary" /> : null
MyChip.propTypes = {
  userName: PropTypes.string,
}

type Props = {
  row: DocListItem
  selected: boolean
  onCheckChange: (id: string, checked: boolean) => void
  openRenameDialog?: (oID: string, name: string) => void
}

function DocumentsTableRow({ row, selected, onCheckChange, openRenameDialog }: Props) {
  const navigate = useNavigate()
  const path = useLocation().pathname
  const page = path.match('deleted') ? 'deleted' : path.match('verify') ? 'task' : 'home'

  const {
    objectID,
    documentName,
    documentTypeName,
    documentStatusNo,
    verifyUserName1,
    verifyUserName2,
    verifyUserName3,
    updateDateTime,
    updateUserName,
    insertDateTime,
    scheduleDocDeletion,
    subGroup,
    assigneeName,
  } = row

  const labelId = `enhanced-table-checkbox-${objectID}`

  const linkTo = {
    pathname: page === 'task' ? '/verify/document-detail' : '../document-detail',
    search: `?id=${objectID}`,
  }
  const linkState = { fromIndexPage: true }
  const handleCellDoubleClick = () => {
    navigate(linkTo, { state: linkState })
  }

  const onCheckboxClick = (_, checked) => {
    onCheckChange(objectID, checked)
  }

  const openRenameDocDialog = () => {
    openRenameDialog!(objectID, documentName)
  }

  // Keep Phase 1 compatibility for now
  const updateDateTimeStr = timeWithoutYear(updateDateTime)
  const scheduleDocDeletionStr = timeWithoutYear(scheduleDocDeletion)
  const insertDateTimeStr = insertDateTime ? timeWithoutYear(insertDateTime) : ''
  const chips = (
    <>
      <MyChip userName={verifyUserName1} />
      <MyChip userName={verifyUserName2} />
      <MyChip userName={verifyUserName3} />
    </>
  )

  const pureTextCells = [
    { key: 'type', value: documentTypeName, pages: ['home', 'deleted', 'task'], multiLine: true },
    { key: 'status', value: <StatusBadge status={documentStatusNo} />, pages: ['home', 'task'] },
    { key: 'verifyUsers', value: chips, pages: ['task'] },
    { key: 'scheduleDocDeletion', value: scheduleDocDeletionStr, pages: ['deleted'] },
    { key: 'insertDateTimeStr', value: insertDateTimeStr, pages: ['task'] },
    { key: 'updateDateTimeStr', value: updateDateTimeStr, pages: ['home', 'deleted', 'task'] },
    { key: 'updateUserName', value: updateUserName, pages: ['home', 'deleted'] },
    { key: 'subGroup', value: subGroup, pages: ['home', 'deleted'] },
    { key: 'assigneeName', value: assigneeName, pages: ['home', 'deleted'] },
  ]
    .filter((x) => x.pages.includes(page))
    .map(({ key, value, multiLine = false }) => (
      <TableCell key={key} onDoubleClick={handleCellDoubleClick}>
        {multiLine ? <OverflowText line={2}>{value}</OverflowText> : value}
      </TableCell>
    ))

  return (
    <TableRow hover aria-checked={selected} tabIndex={-1} key={objectID} id={objectID}>
      <TableCell padding="checkbox">
        <Checkbox
          size="small"
          color="primary"
          checked={selected}
          inputProps={{ 'aria-labelledby': labelId }}
          onChange={onCheckboxClick}
        />
      </TableCell>
      <TableCell
        onDoubleClick={handleCellDoubleClick}
        component="th"
        sx={{ width: '100%', minWidth: 340 }}
        id={labelId}
        scope="row"
      >
        <Box display="flex">
          <NameSpan>
            <Link
              component={RouterLink}
              to={linkTo}
              state={linkState}
              color="inherit"
              sx={{ overflow: 'hiddne' }}
            >
              <OverflowText line={2}>{documentName}</OverflowText>
            </Link>
            {page === 'home' && <EditButton onClick={openRenameDocDialog} />}
          </NameSpan>
        </Box>
      </TableCell>
      {pureTextCells}
    </TableRow>
  )
}

export default DocumentsTableRow
